import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Order, OrderDocument, OrderDocumentType } from '../types';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  createOrderDocument,
  deleteOrderDocument,
  listOrderDocuments,
  updateOrderDocument,
} from '../services/orderDocumentService';

interface OrderDocumentsModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

const DOCUMENT_TYPES: { value: OrderDocumentType; label: string }[] = [
  { value: 'shipping_label', label: 'Shipping Label' },
  { value: 'commercial_invoice', label: 'Commercial Invoice' },
  { value: 'other', label: 'Other' },
];

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

const getTypeLabel = (value: OrderDocumentType): string => {
  return DOCUMENT_TYPES.find((type) => type.value === value)?.label || 'Other';
};

export function OrderDocumentsModal({ order, isOpen, onClose }: OrderDocumentsModalProps) {
  const { userProfile } = useAuth();

  const [documents, setDocuments] = useState<OrderDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<OrderDocumentType>('shipping_label');
  const [uploadTitle, setUploadTitle] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editType, setEditType] = useState<OrderDocumentType>('other');
  const [editTitle, setEditTitle] = useState('');

  const orderLabel = useMemo(() => {
    if (!order) return '';
    return order.orderNumber || order.id;
  }, [order]);

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadType('shipping_label');
    setUploadTitle('');
  };

  const loadDocuments = async () => {
    if (!order) return;

    try {
      setLoading(true);
      const data = await listOrderDocuments(order.id);
      setDocuments(data);
    } catch (error) {
      console.error('Failed to load order documents:', error);
      toast.error('Failed to load order documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !order) return;

    resetUploadForm();
    setEditingId(null);
    loadDocuments();
  }, [isOpen, order?.id]);

  const handleUpload = async () => {
    if (!order) return;
    if (!selectedFile) {
      toast.error('Please select a file to upload.');
      return;
    }

    try {
      setUploading(true);
      await createOrderDocument({
        orderId: order.id,
        file: selectedFile,
        type: uploadType,
        title: uploadTitle,
        uploadedByName: userProfile?.name,
      });
      toast.success('Document attached successfully.');
      resetUploadForm();
      await loadDocuments();
    } catch (error) {
      console.error('Failed to upload order document:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload document.');
    } finally {
      setUploading(false);
    }
  };

  const handleStartEdit = (doc: OrderDocument) => {
    setEditingId(doc.id);
    setEditType(doc.type);
    setEditTitle(doc.title || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditType('other');
    setEditTitle('');
  };

  const handleSaveEdit = async (doc: OrderDocument) => {
    if (!order) return;
    try {
      setSavingId(doc.id);
      await updateOrderDocument(order.id, doc.id, {
        type: editType,
        title: editTitle,
      });
      toast.success('Document updated.');
      handleCancelEdit();
      await loadDocuments();
    } catch (error) {
      console.error('Failed to update order document:', error);
      toast.error('Failed to update document.');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (doc: OrderDocument) => {
    if (!order) return;
    const confirmed = window.confirm(`Delete "${doc.title || doc.fileName}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeletingId(doc.id);
      await deleteOrderDocument(order.id, doc);
      toast.success('Document deleted.');
      await loadDocuments();
    } catch (error) {
      console.error('Failed to delete order document:', error);
      toast.error('Failed to delete document.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenDocument = (doc: OrderDocument) => {
    window.open(doc.downloadUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Order Documents</DialogTitle>
          <DialogDescription>
            Attach and manage documents for order {orderLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={uploadType} onValueChange={(value) => setUploadType(value as OrderDocumentType)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="z-[10003]">
                    {DOCUMENT_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Title (optional)</Label>
                <Input
                  value={uploadTitle}
                  onChange={(event) => setUploadTitle(event.target.value)}
                  placeholder="e.g. DHL Label, Customs Invoice"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>File</Label>
              <Input
                type="file"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
              />
              {selectedFile && (
                <p className="text-xs text-gray-500">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Attach Document
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-white flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Attached Documents</h3>
              <span className="text-sm text-gray-500">{documents.length} total</span>
            </div>

            {loading ? (
              <div className="p-8 flex items-center justify-center text-gray-600">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading documents...
              </div>
            ) : documents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No documents attached yet.</p>
              </div>
            ) : (
              <div className="divide-y">
                {documents.map((doc) => {
                  const isEditing = editingId === doc.id;
                  const isSaving = savingId === doc.id;
                  const isDeleting = deletingId === doc.id;

                  return (
                    <div key={doc.id} className="p-4 space-y-3 bg-white">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="space-y-2">
                          {isEditing ? (
                            <div className="flex flex-col md:flex-row gap-2">
                              <Select value={editType} onValueChange={(value) => setEditType(value as OrderDocumentType)}>
                                <SelectTrigger className="md:w-48">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="z-[10003]">
                                  {DOCUMENT_TYPES.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                value={editTitle}
                                onChange={(event) => setEditTitle(event.target.value)}
                                placeholder="Document title"
                                className="md:w-96"
                              />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900">{doc.title || doc.fileName}</p>
                              <Badge variant="outline">{getTypeLabel(doc.type)}</Badge>
                            </div>
                          )}

                          <p className="text-sm text-gray-500">
                            {doc.fileName} · {formatFileSize(doc.sizeBytes)} · {doc.contentType || 'Unknown type'}
                          </p>
                          <p className="text-xs text-gray-400">
                            Uploaded by {doc.uploadedByName || 'Admin'} on {doc.createdAt.toLocaleString()}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {!isEditing && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleOpenDocument(doc)}>
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Open
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleStartEdit(doc)}>
                                <Pencil className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            </>
                          )}

                          {isEditing && (
                            <>
                              <Button size="sm" onClick={() => handleSaveEdit(doc)} disabled={isSaving}>
                                {isSaving ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Save className="w-4 h-4 mr-1" />
                                )}
                                Save
                              </Button>
                              <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                                <X className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            </>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDelete(doc)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-1" />
                            )}
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
